import * as RDialog from '@radix-ui/react-dialog';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import * as RPopover from '@radix-ui/react-popover';
import * as RSwitch from '@radix-ui/react-switch';
import * as RTooltip from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../cn';

/* ---------------------------------------------------------------- Switch */
export function Switch(props: { checked: boolean; onCheckedChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <RSwitch.Root
      className="bf-switch"
      checked={props.checked}
      onCheckedChange={props.onCheckedChange}
      disabled={props.disabled}
      aria-label={props.label}
    >
      <RSwitch.Thumb className="bf-switch-thumb" />
    </RSwitch.Root>
  );
}

/* ---------------------------------------------------------------- Menú desplegable */
/**
 * Menú desplegable. No bloquea el scroll de la página (modal={false}): bloquearlo saca la barra
 * de scroll y en Windows la pantalla "tiembla" al abrir y cerrar. Se cierra igual al tocar afuera.
 */
export function Menu(props: RMenu.DropdownMenuProps) {
  return <RMenu.Root modal={false} {...props} />;
}
export const MenuTrigger = RMenu.Trigger;

export function MenuContent({ children, align = 'end' }: { children: ReactNode; align?: 'start' | 'end' | 'center' }) {
  return (
    <RMenu.Portal>
      <RMenu.Content className="bf-menu" align={align} sideOffset={8}>
        {children}
      </RMenu.Content>
    </RMenu.Portal>
  );
}

export function MenuItem({
  icon,
  children,
  onSelect,
  danger,
  checked,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  checked?: boolean;
}) {
  return (
    <RMenu.Item className={cn('bf-menu-item', danger && 'bf-menu-item--danger')} onSelect={onSelect}>
      {icon}
      <span>{children}</span>
      {checked && <span className="bf-menu-check">✓</span>}
    </RMenu.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <RMenu.Label className="bf-menu-label">{children}</RMenu.Label>;
}

export function MenuSeparator() {
  return <RMenu.Separator className="bf-menu-sep" />;
}

/* ---------------------------------------------------------------- Popover */
export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
}: {
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <RPopover.Root open={open} onOpenChange={onOpenChange}>
      <RPopover.Trigger asChild>{trigger}</RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content className="bf-popover" align="end" sideOffset={8}>
          {children}
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

/* ---------------------------------------------------------------- Diálogo */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="bf-overlay" />
        <RDialog.Content className={cn('bf-dialog', wide && 'bf-dialog--wide')} aria-describedby={description ? undefined : undefined}>
          <div className="bf-dialog-header">
            <div>
              <RDialog.Title className="bf-dialog-title">{title}</RDialog.Title>
              {description ? (
                <RDialog.Description className="bf-dialog-desc">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="bf-sr-only">{title}</RDialog.Description>
              )}
            </div>
            <RDialog.Close asChild>
              <button className="bf-icon-btn" aria-label="Cerrar">
                <X />
              </button>
            </RDialog.Close>
          </div>
          <div className="bf-dialog-body">{children}</div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/* ---------------------------------------------------------------- Tooltip */
export const TooltipProvider = RTooltip.Provider;

export function Tooltip({ content, children, disabled }: { content: ReactNode; children: ReactNode; disabled?: boolean }) {
  if (disabled) return <>{children}</>;
  return (
    <RTooltip.Root delayDuration={150}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content className="bf-tooltip" side="right" sideOffset={10}>
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}
